import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Versión de la app (se usa para invalidar cache cuando hay actualización)
const APP_VERSION = process.env.REACT_APP_VERSION || '7.4.1';

// Cache TTL para user doc (1 año - el cache se invalida automáticamente por cambio de versión de app)
const USER_CACHE_TTL = 365 * 24 * 60 * 60 * 1000; // 1 año en ms

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false); // Bloqueo global de navegación si se requiere vínculo

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userEmail = firebaseUser.email?.toLowerCase();
        const cacheKey = `user_doc_v${APP_VERSION}_${userEmail}_${firebaseUser.uid}`;

        const fetchAndCacheUser = async (force = false) => {
          try {
            if (!force) {
              const raw = window.localStorage.getItem(cacheKey);
              if (raw) {
                const cached = JSON.parse(raw);
                if (cached && cached.cachedAt && (Date.now() - cached.cachedAt) < USER_CACHE_TTL) {
                  setUser({
                    uid: firebaseUser.uid,
                    email: userEmail,
                    role: cached.role ?? null,
                    nombre: cached.nombre ?? null,
                    canCopyClasses: cached.canCopyClasses ?? false,
                    theme: cached.theme ?? 'light',
                    cachedAt: cached.cachedAt,
                    docId: cached.docId || userEmail // Fallback razonable
                  });
                  setLoading(false);
                  return;
                }
              }
            }

            // Lectura fresca de Firestore
            let docId = userEmail;
            let userSnap = await getDoc(doc(db, 'users', docId));
            if (!userSnap.exists()) {
              docId = firebaseUser.uid;
              userSnap = await getDoc(doc(db, 'users', docId));
            }

            const data = userSnap.exists() ? userSnap.data() : {};
            const role = data.role ?? null;
            const nombre = data.nombre ?? null;
            const canCopyClasses = data.canCopyClasses ?? false;
            let theme = data.theme;
            if (!theme && typeof window !== 'undefined' && window.matchMedia) {
              theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            } else if (!theme) {
              theme = 'light';
            }
            const now = Date.now();

            const userData = {
              uid: firebaseUser.uid,
              email: userEmail,
              role,
              nombre,
              canCopyClasses,
              theme,
              cachedAt: now,
              docId: docId
            };

            setUser(userData);
            window.localStorage.setItem(cacheKey, JSON.stringify({
              role,
              nombre,
              canCopyClasses,
              theme,
              cachedAt: now,
              docId: docId
            }));
            window.localStorage.setItem('userTheme', theme); // Para inyección sincrónica en index.html
            setLoading(false);
          } catch (err) {
            setUser({ uid: firebaseUser.uid, email: firebaseUser.email, role: null, nombre: null, canCopyClasses: false, theme: 'light', cachedAt: 0, docId: userEmail });
            setLoading(false);
          }
        };

        fetchAndCacheUser();
        // Exponer la función de refresco
        window.__refreshUser = () => fetchAndCacheUser(true);

      } else {
        setUser(null);
        setLoading(false);
        delete window.__refreshUser;
      }
    });
    return unsubscribe;
  }, []);

  const refreshUser = React.useCallback(() => {
    if (window.__refreshUser) window.__refreshUser();
  }, []);

  const updateUserTheme = React.useCallback(async (newTheme) => {
    if (!user || !user.docId) return;
    try {
      // Optimistic update
      setUser(prev => ({ ...prev, theme: newTheme }));

      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'users', user.docId), { theme: newTheme });

      // Update cache
      const userEmail = user.email?.toLowerCase();
      const cacheKey = `user_doc_v${APP_VERSION}_${userEmail}_${user.uid}`;
      const raw = window.localStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        cached.theme = newTheme;
        window.localStorage.setItem(cacheKey, JSON.stringify(cached));
      }
      window.localStorage.setItem('userTheme', newTheme); // Para inyección sincrónica en index.html
    } catch (err) {
      console.error("Error updating user theme", err);
      // Revert optimistic update
      refreshUser();
    }
  }, [user, refreshUser]);

  const value = React.useMemo(() => ({ user, loading, isLinking, setIsLinking, refreshUser, updateUserTheme }), [user, loading, isLinking, refreshUser, updateUserTheme]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
